import type {
  CheckStatusResult,
  GenerationJob,
  ImageRequest,
  Model,
  ProviderAdapter,
  VideoRequest,
} from '@byokapp/types'
import { ProviderError } from '@byokapp/types'

const BASE_URL = 'https://api.replicate.com/v1'
const PROVIDER = 'replicate'
const MAX_POLL_MS = 5 * 60 * 1000
const INITIAL_DELAY_MS = 1_000
const MAX_DELAY_MS = 16_000

// ─── Helpers ──────────────────────────────────────────────────────────────────

function authHeaders(apiKey: string): Record<string, string> {
  return {
    Authorization: `Token ${apiKey}`,
    'Content-Type': 'application/json',
  }
}

async function mapHttpError(res: Response): Promise<never> {
  let detail = ''
  try {
    const body = (await res.json()) as { detail?: string }
    detail = body.detail ?? ''
  } catch {
    // body may not be JSON — ignore
  }
  switch (res.status) {
    case 401:
    case 403:
      throw new ProviderError({
        code: 'AUTH_ERROR',
        message: 'Invalid API key.',
        provider: PROVIDER,
        retryable: false,
      })
    case 404:
      throw new ProviderError({
        code: 'NOT_FOUND',
        message: 'Resource not found.',
        provider: PROVIDER,
        retryable: false,
      })
    case 429:
      throw new ProviderError({
        code: 'RATE_LIMITED',
        message: 'Rate limit exceeded. Please try again shortly.',
        provider: PROVIDER,
        retryable: true,
      })
    case 502:
    case 503:
    case 504:
      throw new ProviderError({
        code: 'PROVIDER_DOWN',
        message: 'Replicate is currently unavailable.',
        provider: PROVIDER,
        retryable: true,
      })
    default:
      throw new ProviderError({
        code: 'UNKNOWN',
        message: detail || `Unexpected status: ${res.status}`,
        provider: PROVIDER,
        retryable: false,
      })
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

// ─── Adapter ──────────────────────────────────────────────────────────────────

export class ReplicateAdapter implements ProviderAdapter {
  readonly provider = PROVIDER

  async generateImage(request: ImageRequest, apiKey: string): Promise<GenerationJob> {
    return this.submitPrediction(
      request.model,
      {
        prompt: request.prompt,
        negative_prompt: request.negativePrompt,
        num_outputs: request.numImages ?? 1,
        aspect_ratio: request.aspectRatio ?? '1:1',
      },
      apiKey,
    )
  }

  async generateVideo(request: VideoRequest, apiKey: string): Promise<GenerationJob> {
    return this.submitPrediction(
      request.model,
      {
        image: request.imageUrl,
        prompt: request.prompt,
        duration: request.durationSeconds,
        aspect_ratio: request.aspectRatio,
      },
      apiKey,
    )
  }

  private async submitPrediction(
    version: string,
    input: Record<string, unknown>,
    apiKey: string,
  ): Promise<GenerationJob> {
    const res = await fetch(`${BASE_URL}/predictions`, {
      method: 'POST',
      headers: authHeaders(apiKey),
      body: JSON.stringify({ version, input }),
    })
    if (!res.ok) await mapHttpError(res)
    const data = (await res.json()) as { id: string; status: string }
    return { jobId: data.id, status: toJobStatus(data.status) }
  }

  async checkStatus(jobId: string, apiKey: string): Promise<CheckStatusResult> {
    const res = await fetch(`${BASE_URL}/predictions/${jobId}`, {
      headers: authHeaders(apiKey),
    })
    if (!res.ok) await mapHttpError(res)
    const data = (await res.json()) as { status: string; output?: string[]; error?: string }
    return mapStatusResult(data)
  }

  async listModels(apiKey: string): Promise<Model[]> {
    const [imgRes, vidRes] = await Promise.all([
      fetch(`${BASE_URL}/collections/image-generation`, { headers: authHeaders(apiKey) }),
      fetch(`${BASE_URL}/collections/video-generation`, { headers: authHeaders(apiKey) }),
    ])

    const imgData = imgRes.ok
      ? ((await imgRes.json()) as { results: RawModel[] })
      : { results: [] as RawModel[] }
    const vidData = vidRes.ok
      ? ((await vidRes.json()) as { results: RawModel[] })
      : { results: [] as RawModel[] }

    return [
      ...imgData.results.map((m) => toModel(m, 'image')),
      ...vidData.results.map((m) => toModel(m, 'video')),
    ]
  }

  // Polls checkStatus with exponential backoff until succeeded/failed or timeout.
  // Throws ProviderError(TIMEOUT) after 5 minutes.
  async pollForResult(
    jobId: string,
    apiKey: string,
    signal?: AbortSignal,
  ): Promise<CheckStatusResult> {
    const deadline = Date.now() + MAX_POLL_MS
    let delay = INITIAL_DELAY_MS

    while (true) {
      if (signal?.aborted || Date.now() >= deadline) {
        throw new ProviderError({
          code: 'TIMEOUT',
          message: 'Generation timed out after 5 minutes.',
          provider: PROVIDER,
          retryable: false,
        })
      }

      const result = await this.checkStatus(jobId, apiKey)
      if (result.status === 'succeeded' || result.status === 'failed') return result

      await wait(delay)
      delay = Math.min(delay * 2, MAX_DELAY_MS)
    }
  }
}

// ─── Type helpers ─────────────────────────────────────────────────────────────

interface RawModel {
  name: string
  latest_version?: { id: string }
}

interface RawPrediction {
  status: string
  output?: string[]
  error?: string
}

function toJobStatus(raw: string): GenerationJob['status'] {
  switch (raw) {
    case 'succeeded':
      return 'succeeded'
    case 'failed':
    case 'canceled':
      return 'failed'
    default:
      return 'pending'
  }
}

function mapStatusResult(data: RawPrediction): CheckStatusResult {
  if (data.status === 'succeeded') return { status: 'succeeded', outputUrls: data.output ?? [] }
  if (data.status === 'failed')
    return { status: 'failed', error: data.error ?? 'Generation failed.' }
  return { status: 'pending' }
}

function toModel(m: RawModel, type: 'image' | 'video'): Model {
  return { id: m.latest_version?.id ?? m.name, name: m.name, type, provider: PROVIDER }
}
